// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, { ProductActionsStyle } from "@sc/App/Components/Product/ProductActions";
import ProductBrand, { ProductBrandStyles } from "@sc/Overrides/Components/ProductBrand";
import ProductPrice, { ProductPriceStyles } from "@sc/Overrides/Components/ProductPrice";
import Logo from "@sc/XDL/Icons/Logo";
import React, { FC } from "react";

type Props = HasProductContext;

export interface ProductDetailsSummaryStyles {
    container?: InjectableCss;
    rightSection?: InjectableCss;
    leftSection?: InjectableCss;
    productImageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    logoImage?: IconPresentationProps;
    descriptionWrapper?: InjectableCss;
    productTitle?: TypographyPresentationProps;
    productActionsExtendedStyles?: ProductActionsStyle;
    separator?: InjectableCss;
    productInfo?: InjectableCss;
    productPrice?: ProductPriceStyles;
    productBrand?: ProductBrandStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
}

export const productDetailsSummaryStyles: ProductDetailsSummaryStyles = {};

const styles = productDetailsSummaryStyles;

const ProductDetailsSummary: FC<Props> = ({ productContext }) => {
    const { product } = productContext;

    const { selectedImage, productImage, productSettings, homePageLink } = useAppSelector(state => {
        return {
            selectedImage: getSelectedImage(state, product),
            productImage: product.smallImagePath || product.mediumImagePath || product.largeImagePath,
            productSettings: getSettingsCollection(state).productSettings,
            homePageLink: getHomePageUrl(state),
        };
    });

    if (!product) {
        return null;
    }

    const path =
        selectedImage?.mediumImagePath ||
        selectedImage?.smallImagePath ||
        selectedImage?.largeImagePath ||
        productImage;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftSection}>
                <Link aria-label="Home Page" href={homePageLink}>
                    <Icon {...styles.logoImage} src={Logo} size={56} />
                </Link>
                <StyledWrapper {...styles.separator} />

                <StyledWrapper {...styles.productInfo}>
                    <StyledWrapper {...styles.productImageWrapper}>
                        <LazyImage
                            src={path}
                            altText={product.imageAltText || product.productTitle}
                            {...styles.productImage}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.descriptionWrapper}>
                        {product.brand && (
                            <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.productBrand} />
                        )}
                        <Typography {...styles.productTitle}>{product.productTitle}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <ProductPrice
                    product={productContext}
                    showLabel={false}
                    showSavings={true}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.productPrice}
                />

                <ProductContextAvailability
                    isProductDetailsPage={true}
                    extendedStyles={styles.productAvailabilityStyles}
                />
                <ProductActions extendedStyles={styles.productActionsExtendedStyles} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductDetailsSummary);
